import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatApiError } from '@/utils/apiError';

export function StaffTierCard({ userId }) {
  const [data, setData] = useState(null);
  const [tierId, setTierId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => fetch(`${API_URL}/api/staff/billing/users/${userId}`, { credentials: 'include' })
    .then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) { setData(d); setTierId(d.source === 'staff' ? d.tier.id : ''); setNote(d.note || ''); } }).catch(() => {});
  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;

  const save = async (clear = false) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/billing/users/${userId}/override`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tier_id: clear ? null : tierId || null, note }) });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d));
      setData(d); setTierId(d.source === 'staff' ? d.tier.id : ''); setNote(d.note || '');
      toast.success(clear ? 'Override removed — back to their paid/free plan' : `Set to ${d.tier.name}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const sub = data.subscription;
  return (
    <Card className="p-4" data-testid="staff-tier-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="font-bold text-gray-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> Subscription</h3>
        <span className="text-xs font-bold rounded-full px-2.5 py-1 bg-gray-100 text-gray-800" data-testid="staff-tier-current">{data.tier.name} · {{ stripe: 'paying via Stripe', staff: 'staff override', free: 'free', lapsed: 'lapsed' }[data.source] || data.source}</span>
      </div>
      {sub && <p className="text-xs text-gray-500 mb-2">Stripe: {sub.status}{sub.cancel_at_period_end ? ' (cancelling)' : ''}{sub.current_period_end ? ` · period ends ${new Date(sub.current_period_end).toLocaleDateString('en-GB')}` : ''}</p>}
      <div className="flex flex-wrap gap-2 items-center">
        <select value={tierId} onChange={(e) => setTierId(e.target.value)} className="h-9 border rounded-md px-2 text-sm bg-white" data-testid="staff-tier-select">
          <option value="">No override (use their plan)</option>
          {data.tiers.map((t) => <option key={t.id} value={t.id}>Grant {t.name}{t.price_gbp ? ` (£${t.price_gbp}/mo value)` : ''}</option>)}
        </select>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (e.g. comp'd — top installer)" className="h-9 flex-1 min-w-[180px]" data-testid="staff-tier-note" />
        <Button size="sm" onClick={() => save(false)} disabled={busy || !tierId} className="bg-[#27292E] text-white" data-testid="staff-tier-save">Apply</Button>
        {data.source === 'staff' && <Button size="sm" variant="outline" onClick={() => save(true)} disabled={busy} data-testid="staff-tier-clear">Remove override</Button>}
      </div>
    </Card>
  );
}
